/**
 * Curated library of quality web fonts for the music brand.
 * Each preset exposes a display label + `family` (CSS font-family string) + Google Fonts load URL.
 * Loaded dynamically in ThemeInjector when any slot references them.
 */

export const FONT_PRESETS = [
  // ── Display / Dramatic (great for titles, hero, WORLD) ──
  { id: 'anton', label: 'Anton', family: "'Anton', sans-serif", category: 'display', google: 'Anton' },
  { id: 'bebas-neue', label: 'Bebas Neue', family: "'Bebas Neue', sans-serif", category: 'display', google: 'Bebas+Neue' },
  { id: 'orbitron', label: 'Orbitron', family: "'Orbitron', sans-serif", category: 'display', google: 'Orbitron:wght@400;500;700;900' },
  { id: 'staatliches', label: 'Staatliches', family: "'Staatliches', sans-serif", category: 'display', google: 'Staatliches' },
  { id: 'oswald', label: 'Oswald', family: "'Oswald', sans-serif", category: 'display', google: 'Oswald:wght@300;400;600;700' },
  { id: 'bungee', label: 'Bungee', family: "'Bungee', cursive", category: 'display', google: 'Bungee' },
  { id: 'monoton', label: 'Monoton', family: "'Monoton', cursive", category: 'display', google: 'Monoton' },
  { id: 'audiowide', label: 'Audiowide', family: "'Audiowide', cursive", category: 'display', google: 'Audiowide' },
  { id: 'russo-one', label: 'Russo One', family: "'Russo One', sans-serif", category: 'display', google: 'Russo+One' },
  { id: 'black-ops-one', label: 'Black Ops One', family: "'Black Ops One', cursive", category: 'display', google: 'Black+Ops+One' },
  { id: 'permanent-marker', label: 'Permanent Marker', family: "'Permanent Marker', cursive", category: 'display', google: 'Permanent+Marker' },
  { id: 'press-start-2p', label: 'Press Start 2P', family: "'Press Start 2P', cursive", category: 'display', google: 'Press+Start+2P' },

  // ── Sans (for headings, body) ──
  { id: 'cabinet-grotesk', label: 'Cabinet Grotesk', family: "'Cabinet Grotesk', sans-serif", category: 'sans', google: null }, // self-hosted via index.css
  { id: 'inter', label: 'Inter', family: "'Inter', sans-serif", category: 'sans', google: 'Inter:wght@300;400;500;600;700;800;900' },
  { id: 'poppins', label: 'Poppins', family: "'Poppins', sans-serif", category: 'sans', google: 'Poppins:wght@300;400;500;600;700;800;900' },
  { id: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", category: 'sans', google: 'Montserrat:wght@300;400;500;600;700;800;900' },
  { id: 'raleway', label: 'Raleway', family: "'Raleway', sans-serif", category: 'sans', google: 'Raleway:wght@300;400;500;600;700;800;900' },
  { id: 'work-sans', label: 'Work Sans', family: "'Work Sans', sans-serif", category: 'sans', google: 'Work+Sans:wght@300;400;500;600;700;800;900' },
  { id: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", category: 'sans', google: 'Outfit:wght@300;400;500;600;700;800;900' },
  { id: 'manrope', label: 'Manrope', family: "'Manrope', sans-serif", category: 'sans', google: 'Manrope:wght@300;400;500;600;700;800' },
  { id: 'ibm-plex-sans', label: 'IBM Plex Sans', family: "'IBM Plex Sans', sans-serif", category: 'sans', google: 'IBM+Plex+Sans:wght@300;400;500;600;700' },
  { id: 'dm-sans', label: 'DM Sans', family: "'DM Sans', sans-serif", category: 'sans', google: 'DM+Sans:wght@400;500;700' },

  // ── Serif ──
  { id: 'playfair-display', label: 'Playfair Display', family: "'Playfair Display', serif", category: 'serif', google: 'Playfair+Display:wght@400;500;600;700;800;900' },
  { id: 'bodoni-moda', label: 'Bodoni Moda', family: "'Bodoni Moda', serif", category: 'serif', google: 'Bodoni+Moda:wght@400;500;600;700;800;900' },
  { id: 'dm-serif-display', label: 'DM Serif Display', family: "'DM Serif Display', serif", category: 'serif', google: 'DM+Serif+Display' },
  { id: 'source-serif-pro', label: 'Source Serif Pro', family: "'Source Serif Pro', serif", category: 'serif', google: 'Source+Serif+Pro:wght@400;600;700' },

  // ── Mono (great for prices, stats) ──
  { id: 'jetbrains-mono', label: 'JetBrains Mono', family: "'JetBrains Mono', monospace", category: 'mono', google: 'JetBrains+Mono:wght@400;500;700' },
  { id: 'ibm-plex-mono', label: 'IBM Plex Mono', family: "'IBM Plex Mono', monospace", category: 'mono', google: 'IBM+Plex+Mono:wght@400;500;700' },
  { id: 'space-mono', label: 'Space Mono', family: "'Space Mono', monospace", category: 'mono', google: 'Space+Mono:wght@400;700' },
  { id: 'fira-code', label: 'Fira Code', family: "'Fira Code', monospace", category: 'mono', google: 'Fira+Code:wght@400;500;700' },

  // ── Handwritten / Script ──
  { id: 'caveat', label: 'Caveat', family: "'Caveat', cursive", category: 'script', google: 'Caveat:wght@400;700' },
  { id: 'pacifico', label: 'Pacifico', family: "'Pacifico', cursive", category: 'script', google: 'Pacifico' },
];

export const FONT_SLOT_DEFAULTS = {
  heading: 'cabinet-grotesk',
  body: 'ibm-plex-sans',
  display: 'anton',
  mono: 'ibm-plex-mono',
};

export const FONT_SLOTS = [
  { id: 'heading', label: 'Heading', desc: 'Section titles, large headings (h1, h2)' },
  { id: 'body', label: 'Body', desc: 'Paragraphs, descriptions, default text' },
  { id: 'display', label: 'Display', desc: 'Studio title, WORLD text, marquee' },
  { id: 'mono', label: 'Mono', desc: 'Prices, stats, BPM, overlines' },
];

/**
 * Resolve a font slot's chosen value into a concrete CSS font-family string.
 * Accepts either a preset id, a custom font family name (from uploads), or a raw family.
 */
export function resolveFontFamily(value, customFonts = [], fallback = 'sans-serif') {
  if (!value) return fallback;
  const preset = FONT_PRESETS.find(f => f.id === value);
  if (preset) return preset.family;
  const custom = customFonts.find(f => f.id === value || f.family === value);
  if (custom) return `'${custom.family}', ${fallback}`;
  return value; // already a family string
}
